import React, { useState, useEffect, useCallback } from 'react';
import { collection, getDocs, query, where, doc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiBell, FiSend, FiUsers, FiX, FiCheck, FiAlertCircle, 
  FiRefreshCw, FiTarget, FiMessageSquare, FiSearch 
} from 'react-icons/fi';

/**
 * NotificationManager Component - Admin Panel
 * 
 * Professional notification management system for admins to send targeted
 * push notifications to registered users via Firebase Cloud Messaging.
 * 
 * Key Features:
 * - Send notifications to all registered users or filter by event
 * - Real-time user count and targeting information
 * - Professional notification composer with preview
 * - Integration with Firebase Cloud Messaging via Cloudflare Workers
 * - Notification history and delivery tracking
 * - Responsive design optimized for admin workflow
 * 
 * Security:
 * - Admin authentication required
 * - Input validation and sanitization
 * - Rate limiting to prevent spam
 * 
 * @component
 * @example
 * return (
 *   <NotificationManager />
 * )
 */
const NotificationManager = () => {
  // Core state management
  const [users, setUsers] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showComposer, setShowComposer] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [templateSearch, setTemplateSearch] = useState('');
  
  // Notification composition state
  const [notification, setNotification] = useState({
    title: '',
    body: '',
    targetAudience: 'all', // 'all', 'event', 'registered'
    selectedEvent: '',
    scheduledFor: '', // For future scheduling feature
    priority: 'normal' // 'high', 'normal', 'low'
  });
  
  // UI state
  const [filters] = useState({
    hasNotifications: true,
    hasRegistrations: false,
    eventId: ''
  });
  const [targetCount, setTargetCount] = useState(0);
  const [feedback, setFeedback] = useState({ type: '', message: '' });

  // Pre-built notification templates for common scenarios
  const notificationTemplates = [
    {
      id: 'event-reminder',
      name: 'Event Reminder',
      category: 'Events',
      icon: '📅',
      title: 'Upcoming Event: {eventTitle}',
      body: 'Don\'t forget! Your registered event "{eventTitle}" is coming up soon. We\'re excited to see you there!',
      targetAudience: 'event',
      priority: 'high'
    },
    {
      id: 'registration-confirmation',
      name: 'Registration Confirmation',
      category: 'Registration',
      icon: '✅',
      title: 'Registration Confirmed - {eventTitle}',
      body: 'Great news! Your registration for "{eventTitle}" has been confirmed. Check your email for more details.',
      targetAudience: 'event',
      priority: 'high'
    },
    {
      id: 'event-update',
      name: 'Event Update',
      category: 'Updates',
      icon: '📢',
      title: 'Important Update: {eventTitle}',
      body: 'We have an important update regarding "{eventTitle}". Please check the event details for the latest information.',
      targetAudience: 'event',
      priority: 'high'
    },
    {
      id: 'last-chance',
      name: 'Last Chance Registration',
      category: 'Promotional',
      icon: '⏰',
      title: 'Last Chance to Register!',
      body: 'Registration for our amazing events is closing soon! Don\'t miss out on this opportunity to join YouthXConnect.',
      targetAudience: 'all',
      priority: 'high'
    },
    {
      id: 'thank-you',
      name: 'Thank You Message',
      category: 'Engagement',
      icon: '🙏',
      title: 'Thank You for Joining Us!',
      body: 'Thank you for being part of the YouthXConnect community! Your participation makes our events special.',
      targetAudience: 'registered',
      priority: 'normal'
    },
    {
      id: 'workshop-announcement',
      name: 'New Workshop Announcement',
      category: 'Promotional',
      icon: '🎯',
      title: 'New Workshop Available!',
      body: 'We\'ve added an exciting new workshop to our lineup! Check out the latest opportunities to learn and grow.',
      targetAudience: 'all',
      priority: 'normal'
    },
    {
      id: 'payment-reminder',
      name: 'Payment Reminder',
      category: 'Admin',
      icon: '💳',
      title: 'Payment Reminder - {eventTitle}',
      body: 'Friendly reminder: Your payment for "{eventTitle}" is pending. Please complete your payment to secure your spot.',
      targetAudience: 'event',
      priority: 'high'
    },
    {
      id: 'event-cancelled',
      name: 'Event Cancellation',
      category: 'Admin',
      icon: '❌',
      title: 'Event Cancelled: {eventTitle}',
      body: 'We regret to inform you that "{eventTitle}" has been cancelled. We\'ll be issuing full refunds within 3-5 business days.',
      targetAudience: 'event',
      priority: 'high'
    },
    {
      id: 'general-announcement',
      name: 'General Announcement',
      category: 'General',
      icon: '📣',
      title: 'Important Announcement',
      body: 'We have an important announcement to share with our YouthXConnect community. Stay tuned for exciting updates!',
      targetAudience: 'all',
      priority: 'normal'
    },
    {
      id: 'feedback-request',
      name: 'Feedback Request',
      category: 'Engagement',
      icon: '💬',
      title: 'Share Your Feedback',
      body: 'We value your opinion! Please take a moment to share your feedback about your recent YouthXConnect experience.',
      targetAudience: 'registered',
      priority: 'low'
    }
  ];

  /**
   * Get all successful registrations for a specific user
   * @param {string} userId - The user ID to fetch registrations for
   * @returns {Array} Array of user registrations
   */
  const getUserRegistrations = async (userId) => {
    try {
      const eventsSnapshot = await getDocs(collection(db, 'events'));
      const userRegistrations = [];

      for (const eventDoc of eventsSnapshot.docs) {
        const eventId = eventDoc.id;
        const registrationsRef = collection(db, `registrations/${eventId}/registrations`);
        const userRegsQuery = query(registrationsRef, where('userId', '==', userId));
        const userRegsSnapshot = await getDocs(userRegsQuery);

        userRegsSnapshot.forEach(regDoc => {
          const regData = regDoc.data();
          if (regData.status === 'success') {
            userRegistrations.push({
              eventId,
              registrationId: regDoc.id,
              eventTitle: eventDoc.data().title || eventDoc.data().eventTitle,
              registrationDate: regData.timestamp
            });
          }
        });
      }

      return userRegistrations;
    } catch (error) {
      console.error('Error fetching user registrations:', error);
      return [];
    }
  };

  /**
   * Load all users with notification preferences and FCM tokens
   * Only includes users who have granted notification permission
   */
  const loadUsers = useCallback(async () => {
    try {
      const userPrefsSnapshot = await getDocs(collection(db, 'userNotificationPrefs'));
      const usersWithNotifications = [];

      userPrefsSnapshot.forEach(doc => {
        const prefs = doc.data();
        if (prefs.enabled && prefs.fcmToken && prefs.response === 'granted') {
          usersWithNotifications.push({
            uid: doc.id,
            fcmToken: prefs.fcmToken,
            preferences: prefs
          });
        }
      });

      // Enrich user data with registration information
      const enrichedUsers = await Promise.all(
        usersWithNotifications.map(async (user) => {
          const registrations = await getUserRegistrations(user.uid);
          return {
            ...user,
            registrations,
            registrationCount: registrations.length
          };
        })
      );

      setUsers(enrichedUsers);
      console.log(`Loaded ${enrichedUsers.length} users with notification permissions`);
    } catch (error) {
      console.error('Error loading users:', error);
      throw error;
    }
  }, []);

  /**
   * Load all events for targeting options
   */
  const loadEvents = async () => {
    try {
      const eventsSnapshot = await getDocs(collection(db, 'events'));
      const eventsList = eventsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setEvents(eventsList);
    } catch (error) {
      console.error('Error loading events:', error);
      throw error;
    }
  };

  /**
   * Calculate the number of users who will receive the notification
   * Based on current targeting settings and filters
   */
  const calculateTargetCount = useCallback(() => {
    let targetUsers = users;

    // Apply audience targeting
    if (notification.targetAudience === 'event' && notification.selectedEvent) {
      targetUsers = users.filter(user => 
        user.registrations.some(reg => reg.eventId === notification.selectedEvent)
      );
    } else if (notification.targetAudience === 'registered') {
      targetUsers = users.filter(user => user.registrationCount > 0);
    }

    // Apply additional filters
    if (filters.hasRegistrations) {
      targetUsers = targetUsers.filter(user => user.registrationCount > 0);
    }

    if (filters.eventId) {
      targetUsers = targetUsers.filter(user =>
        user.registrations.some(reg => reg.eventId === filters.eventId)
      );
    }

    setTargetCount(targetUsers.length);
  }, [users, notification, filters]);

  /**
   * Load users with notification tokens and events on component mount
   * Fetches user data and calculates targeting metrics
   */
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        await Promise.all([loadUsers(), loadEvents()]);
      } catch (error) {
        console.error('Error loading notification data:', error);
        setFeedback({ 
          type: 'error', 
          message: 'Failed to load notification data. Please refresh.' 
        });
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [loadUsers]);

  /**
   * Calculate target audience count when filters or notification settings change
   */
  useEffect(() => {
    calculateTargetCount();
  }, [users, notification, filters, calculateTargetCount]);

  /**
   * Validate notification content before sending
   * @returns {Object} Validation result with success status and errors
   */
  const validateNotification = () => {
    const errors = [];

    if (!notification.title.trim()) {
      errors.push('Title is required');
    } else if (notification.title.length > 100) {
      errors.push('Title must be 100 characters or less');
    }

    if (!notification.body.trim()) {
      errors.push('Message body is required');
    } else if (notification.body.length > 500) {
      errors.push('Message body must be 500 characters or less');
    }

    if (notification.targetAudience === 'event' && !notification.selectedEvent) {
      errors.push('Please select an event for targeted notifications');
    }

    if (targetCount === 0) {
      errors.push('No users will receive this notification with current settings');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  };

  /**
   * Send notification via Cloudflare Worker
   * Handles the actual notification delivery through Firebase Cloud Messaging
   */
  const sendNotification = async () => {
    const validation = validateNotification();
    if (!validation.isValid) {
      setFeedback({
        type: 'error',
        message: `Validation failed: ${validation.errors.join(', ')}`
      });
      return;
    }

    setSending(true);
    setFeedback({ type: '', message: '' });

    try {
      // Calculate target users based on current settings
      let targetUsers = users;
      if (notification.targetAudience === 'event' && notification.selectedEvent) {
        targetUsers = users.filter(user => 
          user.registrations.some(reg => reg.eventId === notification.selectedEvent)
        );
      } else if (notification.targetAudience === 'registered') {
        targetUsers = users.filter(user => user.registrationCount > 0);
      }

      // Prepare notification payload with placeholder replacement
      const finalTitle = replacePlaceholders(notification.title, notification.selectedEvent);
      const finalBody = replacePlaceholders(notification.body, notification.selectedEvent);
      
      const notificationPayload = {
        title: finalTitle,
        body: finalBody,
        icon: '/logo192.png',
        badge: '/favicon.ico',
        data: {
          timestamp: new Date().toISOString(),
          type: 'admin_notification',
          priority: notification.priority,
          eventId: notification.selectedEvent || null
        }
      };

      // Send to Cloudflare Worker for processing
      const response = await fetch('/api/send-notification', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}` // Admin auth
        },
        body: JSON.stringify({
          notification: notificationPayload,
          tokens: targetUsers.map(user => user.fcmToken),
          targetAudience: notification.targetAudience,
          eventId: notification.selectedEvent || null
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      // Log notification to Firestore for history
      await logNotification({
        ...notification,
        targetCount: targetUsers.length,
        sentAt: new Date().toISOString(),
        sentBy: 'admin', // In real app, get from auth context
        deliveryStatus: result.success ? 'sent' : 'failed',
        deliveryDetails: result
      });

      setFeedback({
        type: 'success',
        message: `Notification sent successfully to ${targetUsers.length} users!`
      });

      // Reset form
      setNotification({
        title: '',
        body: '',
        targetAudience: 'all',
        selectedEvent: '',
        scheduledFor: '',
        priority: 'normal'
      });
      setShowComposer(false);

    } catch (error) {
      console.error('Error sending notification:', error);
      setFeedback({
        type: 'error',
        message: `Failed to send notification: ${error.message}`
      });
    } finally {
      setSending(false);
    }
  };

  /**
   * Log notification for history and analytics
   * @param {Object} notificationData - The notification data to log
   */
  const logNotification = async (notificationData) => {
    try {
      const notificationId = `notification_${Date.now()}`;
      await setDoc(doc(db, 'notificationHistory', notificationId), {
        ...notificationData,
        id: notificationId
      });
    } catch (error) {
      console.error('Error logging notification:', error);
    }
  };

  /**
   * Apply a pre-built template to the notification form
   * @param {Object} template - The template to apply
   */
  const applyTemplate = (template) => {
    setNotification(prev => ({
      ...prev,
      title: template.title,
      body: template.body,
      targetAudience: template.targetAudience,
      priority: template.priority,
      selectedEvent: template.targetAudience === 'event' ? prev.selectedEvent : ''
    }));
    setShowTemplates(false);
    setShowComposer(true);
  };

  /**
   * Replace placeholders in notification text with actual event data
   * @param {string} text - Text containing placeholders
   * @param {string} eventId - ID of the selected event
   * @returns {string} Text with placeholders replaced
   */
  const replacePlaceholders = (text, eventId) => {
    if (!eventId) return text;
    
    const event = events.find(e => e.id === eventId);
    if (!event) return text;
    
    return text
      .replace(/{eventTitle}/g, event.title || event.eventTitle || 'Event')
      .replace(/{eventDate}/g, event.date || 'TBD')
      .replace(/{eventTime}/g, event.time || 'TBD');
  };

  /**
   * Clear feedback messages after a delay
   */
  useEffect(() => {
    if (feedback.message) {
      const timer = setTimeout(() => {
        setFeedback({ type: '', message: '' });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [feedback]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <FiRefreshCw className="animate-spin text-4xl text-indigo-600 mb-4 mx-auto" />
          <p className="text-gray-600">Loading notification system...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              <FiBell className="mr-3 text-indigo-600" />
              Notification Manager
            </h1>
            <p className="text-gray-600 mt-2">
              Send targeted notifications to users who have registered for events
            </p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => setShowTemplates(true)}
              className="bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 transition-colors flex items-center"
            >
              <svg className="mr-2 w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M4 3a2 2 0 100 4h12a2 2 0 100-4H4zM4 9a2 2 0 100 4h12a2 2 0 100-4H4zM4 15a2 2 0 100 4h12a2 2 0 100-4H4z"/>
              </svg>
              Use Template
            </button>
            <button
              onClick={() => setShowComposer(true)}
              className="bg-indigo-600 text-white px-6 py-3 rounded-lg hover:bg-indigo-700 transition-colors flex items-center"
            >
              <FiMessageSquare className="mr-2" />
              Custom Notification
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center">
            <FiUsers className="text-2xl text-blue-600 mr-3" />
            <div>
              <p className="text-sm text-gray-600">Users with Notifications</p>
              <p className="text-2xl font-bold text-gray-900">{users.length}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center">
            <FiTarget className="text-2xl text-green-600 mr-3" />
            <div>
              <p className="text-sm text-gray-600">Current Target</p>
              <p className="text-2xl font-bold text-gray-900">{targetCount}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center">
            <FiSend className="text-2xl text-purple-600 mr-3" />
            <div>
              <p className="text-sm text-gray-600">Ready to Send</p>
              <p className="text-2xl font-bold text-gray-900">
                {notification.title && notification.body ? 'Yes' : 'No'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Feedback Messages */}
      <AnimatePresence>
        {feedback.message && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className={`mb-6 p-4 rounded-lg flex items-center ${
              feedback.type === 'success'
                ? 'bg-green-50 text-green-800 border border-green-200'
                : feedback.type === 'error'
                ? 'bg-red-50 text-red-800 border border-red-200'
                : 'bg-blue-50 text-blue-800 border border-blue-200'
            }`}
          >
            {feedback.type === 'success' ? (
              <FiCheck className="mr-3 text-green-600" />
            ) : feedback.type === 'error' ? (
              <FiAlertCircle className="mr-3 text-red-600" />
            ) : (
              <FiBell className="mr-3 text-blue-600" />
            )}
            <span>{feedback.message}</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Notification Composer Modal */}
      <AnimatePresence>
        {showComposer && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6">
                {/* Modal Header */}
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">Compose Notification</h2>
                  <button
                    onClick={() => setShowComposer(false)}
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                  >
                    <FiX className="text-xl text-gray-500" />
                  </button>
                </div>

                {/* Notification Form */}
                <div className="space-y-6">
                  {/* Title */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Notification Title *
                    </label>
                    <input
                      type="text"
                      value={notification.title}
                      onChange={(e) => setNotification(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="Enter notification title..."
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      maxLength={100}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {notification.title.length}/100 characters
                    </p>
                  </div>

                  {/* Body */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Message Body *
                    </label>
                    <textarea
                      value={notification.body}
                      onChange={(e) => setNotification(prev => ({ ...prev, body: e.target.value }))}
                      placeholder="Enter your message..."
                      rows={4}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      maxLength={500}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {notification.body.length}/500 characters
                    </p>
                  </div>

                  {/* Target Audience */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Target Audience
                    </label>
                    <select
                      value={notification.targetAudience}
                      onChange={(e) => setNotification(prev => ({ 
                        ...prev, 
                        targetAudience: e.target.value,
                        selectedEvent: e.target.value !== 'event' ? '' : prev.selectedEvent
                      }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="all">All Users with Notifications Enabled</option>
                      <option value="registered">Users with Event Registrations</option>
                      <option value="event">Users Registered for Specific Event</option>
                    </select>
                  </div>

                  {/* Event Selection */}
                  {notification.targetAudience === 'event' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Select Event
                      </label>
                      <select
                        value={notification.selectedEvent}
                        onChange={(e) => setNotification(prev => ({ ...prev, selectedEvent: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        <option value="">Select an event...</option>
                        {events.map(event => (
                          <option key={event.id} value={event.id}>
                            {event.title || event.eventTitle}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Priority */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Priority Level
                    </label>
                    <select
                      value={notification.priority}
                      onChange={(e) => setNotification(prev => ({ ...prev, priority: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="normal">Normal</option>
                      <option value="high">High Priority</option>
                      <option value="low">Low Priority</option>
                    </select>
                  </div>

                  {/* Notification Preview */}
                  {notification.title && notification.body && (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                        <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M10 12a2 2 0 100-4 2 2 0 000 4z"/>
                          <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd"/>
                        </svg>
                        Preview
                      </h4>
                      <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm">
                        <div className="flex items-start space-x-3">
                          <div className="flex-shrink-0">
                            <img src="/logo192.png" alt="YouthXConnect" className="w-8 h-8 rounded" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900">
                              {replacePlaceholders(notification.title, notification.selectedEvent)}
                            </p>
                            <p className="text-sm text-gray-600 mt-1">
                              {replacePlaceholders(notification.body, notification.selectedEvent)}
                            </p>
                            <p className="text-xs text-gray-400 mt-2">
                              YouthXConnect Events • Now
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Target Count Display */}
                  <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                    <div className="flex items-center">
                      <FiTarget className="text-indigo-600 mr-2" />
                      <span className="text-indigo-800 font-medium">
                        This notification will be sent to {targetCount} users
                      </span>
                    </div>
                  </div>
                </div>

                {/* Modal Actions */}
                <div className="flex justify-end space-x-4 mt-8 pt-6 border-t border-gray-200">
                  <button
                    onClick={() => setShowComposer(false)}
                    className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={sendNotification}
                    disabled={sending || !notification.title || !notification.body || targetCount === 0}
                    className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {sending ? (
                      <>
                        <FiRefreshCw className="animate-spin mr-2" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <FiSend className="mr-2" />
                        Send Notification
                      </>
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Templates Modal */}
      <AnimatePresence>
        {showTemplates && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6">
                                 {/* Modal Header */}
                 <div className="flex items-center justify-between mb-6">
                   <div>
                     <h2 className="text-2xl font-bold text-gray-900">Notification Templates</h2>
                     <p className="text-gray-600 mt-1">Choose from pre-built templates for common scenarios</p>
                   </div>
                   <button
                     onClick={() => setShowTemplates(false)}
                     className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                   >
                     <FiX className="text-xl text-gray-500" />
                   </button>
                 </div>

                 {/* Search and Filter */}
                 <div className="mb-6">
                   <div className="relative">
                     <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                     <input
                       type="text"
                       value={templateSearch}
                       onChange={(e) => setTemplateSearch(e.target.value)}
                       placeholder="Search templates by name, category, or content..."
                       className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                     />
                   </div>
                 </div>

                                 {/* Templates Grid */}
                 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                   {(() => {
                     const filteredTemplates = notificationTemplates.filter(template => 
                       templateSearch === '' || 
                       template.name.toLowerCase().includes(templateSearch.toLowerCase()) ||
                       template.category.toLowerCase().includes(templateSearch.toLowerCase()) ||
                       template.title.toLowerCase().includes(templateSearch.toLowerCase()) ||
                       template.body.toLowerCase().includes(templateSearch.toLowerCase())
                     );

                     if (filteredTemplates.length === 0) {
                       return (
                         <div className="col-span-full text-center py-8">
                           <FiSearch className="mx-auto text-4xl text-gray-300 mb-4" />
                           <p className="text-gray-500">No templates found matching "{templateSearch}"</p>
                           <button
                             onClick={() => setTemplateSearch('')}
                             className="text-indigo-600 hover:text-indigo-800 text-sm mt-2"
                           >
                             Clear search
                           </button>
                         </div>
                       );
                     }

                     return filteredTemplates.map((template) => (
                    <motion.div
                      key={template.id}
                      whileHover={{ scale: 1.02 }}
                      className="border border-gray-200 rounded-lg p-4 hover:border-indigo-300 hover:shadow-md transition-all cursor-pointer"
                      onClick={() => applyTemplate(template)}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center">
                          <span className="text-2xl mr-3">{template.icon}</span>
                          <div>
                            <h3 className="font-semibold text-gray-900">{template.name}</h3>
                            <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                              {template.category}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center">
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            template.priority === 'high' ? 'bg-red-100 text-red-600' :
                            template.priority === 'low' ? 'bg-green-100 text-green-600' :
                            'bg-blue-100 text-blue-600'
                          }`}>
                            {template.priority}
                          </span>
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <div>
                          <p className="text-sm text-gray-600 mb-1">Title:</p>
                          <p className="text-sm font-medium text-gray-900 bg-gray-50 p-2 rounded">
                            {template.title}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600 mb-1">Message:</p>
                          <p className="text-sm text-gray-700 bg-gray-50 p-2 rounded">
                            {template.body}
                          </p>
                        </div>
                        <div className="flex items-center justify-between text-xs text-gray-500 pt-2">
                          <span>Target: {template.targetAudience === 'all' ? 'All Users' : 
                                       template.targetAudience === 'registered' ? 'Registered Users' : 
                                       'Event Specific'}</span>
                          <button className="text-indigo-600 hover:text-indigo-800 font-medium">
                            Use Template →
                          </button>
                        </div>
                      </div>
                                         </motion.div>
                   ));
                   })()}
                 </div>

                {/* Template Categories Legend */}
                <div className="mt-6 pt-4 border-t border-gray-200">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Categories:</h4>
                  <div className="flex flex-wrap gap-2">
                    {[...new Set(notificationTemplates.map(t => t.category))].map(category => (
                      <span key={category} className="text-xs bg-gray-100 text-gray-600 px-3 py-1 rounded-full">
                        {category}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Help Text */}
                <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-start">
                    <svg className="w-5 h-5 text-blue-600 mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <h5 className="text-sm font-medium text-blue-900">Template Tips:</h5>
                      <ul className="text-sm text-blue-700 mt-1 space-y-1">
                        <li>• Templates with {'{eventTitle}'} will be replaced with actual event names</li>
                        <li>• You can customize any template after selection</li>
                        <li>• Templates are categorized by use case for easy browsing</li>
                        <li>• Priority levels help determine notification urgency</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NotificationManager; 